import React, { useEffect, useState } from 'react';

import { InlineButton } from '@/components/button';
import { Icon } from '@/components/icon';
import { SortableItemSimple } from '@/explore/components/sortable-item';
import { getExplorationTabs } from '@/core/exploration';

import { Exploration } from '../../types';
import { ExplorationOutlineTab } from './exploration-outline-tab';
import { CellItem } from './cell-item';
import { TabEditForm } from './tab-edit-form';
import { getCellsForExplorationOutline } from './utils';

import styles from './exploration-outline.module.scss';
import formStyles from '@/components/form/form.module.scss';

type ExplorationOutlineProps = {
  exploration: Exploration;
  activeTabId: string | null;
  onSelectCell: (cellId: string) => void;
  onAddCell: (index: number, tabId?: string) => void;
  onAddTab: (name: string) => void;
  onDeleteTab: (tabId: string) => void;
  onRenameTab: (tabId: string, name: string) => void;
  onDuplicateTab: (tabId: string) => void;
  onMoveCell: (cellId: string, targetId: string, afterTarget: boolean) => void;
  onMoveTab: (fromIndex: number, toIndex: number, after: boolean) => void;
  renamingTabId: string | null;
  onSetRenamingTab: (tabId: string) => void;
  onClearRenamingTab: () => void;
};

export const ExplorationOutline = (props: ExplorationOutlineProps) => {
  const {
    exploration,
    activeTabId,
    onSelectCell,
    onAddCell,
    onAddTab,
    onDeleteTab,
    onRenameTab,
    onDuplicateTab,
    onMoveCell,
    onMoveTab,
    renamingTabId,
    onSetRenamingTab,
    onClearRenamingTab,
  } = props;
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isAddingTab, setIsAddingTab] = useState(false);
  const [expandedTabs, setExpandedTabs] = useState<string[]>([]);
  const [activeTabCollapsed, setActiveTabCollapsed] = useState(false);

  const tabs = getExplorationTabs(exploration);
  const hasMultipleTabs = tabs !== undefined && tabs.length > 1;
  const allCells = getCellsForExplorationOutline(exploration);

  useEffect(() => {
    setActiveTabCollapsed(false);
  }, [activeTabId]);

  const handleToggleTab = (tabId: string) => {
    if (activeTabId === tabId) {
      setActiveTabCollapsed((prev) => !prev);
      setExpandedTabs((prev) => prev.filter((id) => id !== tabId));
      return;
    }
    setExpandedTabs((prev) =>
      prev.includes(tabId) ? prev.filter((id) => id !== tabId) : [...prev, tabId],
    );
  };

  const handleMoveCell = (fromIndex: number, toIndex: number, tabId?: string) => {
    const cells =
      tabId !== undefined
        ? getCellsForExplorationOutline(exploration, tabId)
        : exploration.view.cells;
    const fromCell = cells.at(fromIndex);
    const toCell = cells.at(toIndex);
    if (fromCell && toCell) {
      onMoveCell(fromCell.id, toCell.id, toIndex > fromIndex);
    }
  };

  return (
    <div className={styles.explorationOutline}>
      {hasMultipleTabs ? (
        tabs.map((tab, i) => (
          <React.Fragment key={tab.tabId}>
            <SortableItemSimple
              scope="exploration-outline-tabs"
              index={i}
              draggedIndex={draggedIndex}
              setDraggedIndex={setDraggedIndex}
              onReorder={onMoveTab}
              classNames={{
                dragBefore: styles.dragBefore,
                dragAfter: styles.dragAfter,
              }}>
              <ExplorationOutlineTab
                tab={tab}
                isActive={tab.tabId === activeTabId}
                exploration={exploration}
                draggedIndex={draggedIndex}
                setDraggedIndex={setDraggedIndex}
                onMoveCell={handleMoveCell}
                onSelectCell={onSelectCell}
                onAddCell={onAddCell}
                onDeleteTab={onDeleteTab}
                onRenameTab={onRenameTab}
                onDuplicateTab={onDuplicateTab}
                isExpanded={
                  (tab.tabId === activeTabId && !activeTabCollapsed) ||
                  expandedTabs.includes(tab.tabId)
                }
                onToggleCollapsed={() => handleToggleTab(tab.tabId)}
                renamingTabId={renamingTabId}
                onSetRenamingTab={onSetRenamingTab}
                onClearRenamingTab={onClearRenamingTab}
              />
            </SortableItemSimple>
            <hr className={formStyles.dashed} />
          </React.Fragment>
        ))
      ) : (
        <>
          <div className={styles.group}>
            {allCells.map((cell, i) => (
              <CellItem
                key={cell.id}
                cell={cell}
                index={i}
                draggedIndex={draggedIndex}
                setDraggedIndex={setDraggedIndex}
                onMove={handleMoveCell}
                onClickCell={onSelectCell}
              />
            ))}
            {allCells.length === 0 && (
              <div className={styles.addButton}>
                <InlineButton onClick={() => onAddCell(allCells.length)} size="small">
                  <Icon name="Plus" size={15} /> New block
                </InlineButton>
              </div>
            )}
          </div>
          <hr className={formStyles.dashed} />
        </>
      )}
      {isAddingTab ? (
        <div className={styles.group}>
          <div className={styles.groupHeader}>
            <TabEditForm
              onSave={(name) => {
                onAddTab(name);
                setIsAddingTab(false);
              }}
              onCancel={() => setIsAddingTab(false)}
            />
          </div>
        </div>
      ) : (
        <div className={styles.group}>
          <div className={styles.addButton}>
            <InlineButton onClick={() => setIsAddingTab(true)} size="small">
              <Icon name="Plus" size={15} /> New Tab
            </InlineButton>
          </div>
        </div>
      )}
    </div>
  );
};
